import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Building2, FlaskConical, Users, Award, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Reveal, PageHero } from "@/components/Reveal";
import { Field } from "@/components/Field";
import { api } from "@/lib/api";
import { IMG } from "@/lib/content";
import { useLang } from "@/context/LanguageContext";
import { SchoolOnboard } from "@/components/admission/SchoolOnboard";

const benefits = [
  { icon: FlaskConical, t: ["Turnkey Tech Lab", "संपूर्ण टेक लैब"], d: ["Complete AI, Robotics, Drone & Aerospace lab setup with kits and furniture.", "किट और फ़र्नीचर के साथ पूरी AI, रोबोटिक्स, ड्रोन और एयरोस्पेस लैब।"] },
  { icon: Users, t: ["Trained Instructors", "प्रशिक्षित प्रशिक्षक"], d: ["RoboSpark-certified trainers delivering weekly sessions on campus.", "RoboSpark-प्रमाणित ट्रेनर कैंपस में साप्ताहिक सत्र लेते हैं।"] },
  { icon: Award, t: ["Certifications & Competitions", "प्रमाणपत्र और प्रतियोगिताएँ"], d: ["Student certificates, inter-school contests and national exposure.", "छात्र प्रमाणपत्र, अंतर-विद्यालय प्रतियोगिताएँ और राष्ट्रीय मंच।"] },
  { icon: Building2, t: ["NEP 2020 Aligned", "NEP 2020 के अनुरूप"], d: ["Curriculum aligned with NEP skill education and ATL objectives.", "पाठ्यक्रम NEP कौशल शिक्षा और ATL उद्देश्यों के अनुरूप।"] },
];

const empty = { school_name: "", contact_person: "", designation: "", email: "", phone: "", city: "", state: "", student_count: "", message: "" };

export default function SchoolPartnership() {
  const { L } = useLang();
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 150);
  }, [hash]);
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/forms/partnership", { ...f, student_count: Number(f.student_count) || 0 });
      toast.success(L("Thank you! Our partnerships team will contact you within 48 hours.", "धन्यवाद! हमारी साझेदारी टीम 48 घंटों में आपसे संपर्क करेगी।"));
      setF(empty);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || L("Submission failed. Please check the fields.", "जमा नहीं हुआ। कृपया जानकारी जाँचें।"));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="schools-page">
      <PageHero chip={L("School Partnership", "स्कूल साझेदारी")} title={L("Turn your school into an innovation hub.", "अपने स्कूल को नवाचार केंद्र बनाएँ।")} subtitle={L("Join 120+ schools in Bihar, West Bengal, Jharkhand and across India.", "बिहार, पश्चिम बंगाल, झारखंड और पूरे भारत के 120+ स्कूलों से जुड़ें।")} image={IMG.robot} testid="schools-hero" />
      <section className="bg-[#F0F4F8] text-[#0A192F] py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {benefits.map((b, i) => (
            <Reveal key={b.t[0]} delay={0.08 * i}>
              <div className="p-6 rounded-2xl border border-slate-200 bg-white shadow-[0_8px_30px_rgba(10,25,47,0.06)] h-full"><b.icon className="w-7 h-7 text-[#007A8A]" /><h3 className="font-bold text-lg mt-3">{L(...b.t)}</h3><p className="text-slate-600 text-sm mt-1">{L(...b.d)}</p></div>
            </Reveal>
          ))}
        </div>
      </section>
      <section id="onboard" className="bg-[#F0F4F8] py-20 scroll-mt-20" data-testid="school-onboard-section">
        <div className="max-w-3xl mx-auto px-5 lg:px-8"><SchoolOnboard /></div>
      </section>
      <section className="bg-[#E8F0FE] py-20">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-[#0A192F]">{L("Request a partnership proposal", "साझेदारी प्रस्ताव का अनुरोध करें")}</h2>
          <p className="text-slate-600 mt-2">{L("Principals and coordinators — share a few details and we'll send a customised proposal.", "प्रधानाचार्य और समन्वयक — कुछ जानकारी दें, हम आपके लिए विशेष प्रस्ताव भेजेंगे।")}</p>
          <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-5 bg-white p-6 md:p-8 rounded-2xl border border-slate-200" data-testid="partnership-form">
            <Field id="school_name" label={L("School name", "स्कूल का नाम")} required value={f.school_name} onChange={on("school_name")} />
            <Field id="contact_person" label={L("Contact person", "संपर्क व्यक्ति")} required value={f.contact_person} onChange={on("contact_person")} />
            <Field id="designation" label={L("Designation", "पद")} placeholder={L("Principal / Coordinator", "प्रधानाचार्य / समन्वयक")} value={f.designation} onChange={on("designation")} />
            <Field id="p_email" label={L("Email", "ईमेल")} type="email" required value={f.email} onChange={on("email")} />
            <Field id="p_phone" label={L("Phone", "फ़ोन")} required value={f.phone} onChange={on("phone")} />
            <Field id="student_count" label={L("Number of students", "छात्रों की संख्या")} type="number" value={f.student_count} onChange={on("student_count")} />
            <Field id="p_city" label={L("City", "शहर")} required value={f.city} onChange={on("city")} />
            <Field id="p_state" label={L("State", "राज्य")} required value={f.state} onChange={on("state")} />
            <div className="sm:col-span-2 space-y-1.5">
              <label className="text-sm font-semibold text-[#0A192F]" htmlFor="p_message">{L("Message", "संदेश")}</label>
              <Textarea id="p_message" rows={4} value={f.message} onChange={on("message")} data-testid="input-p_message" />
            </div>
            <Button type="submit" disabled={loading} className="sm:col-span-2 rs-cta h-12 rounded-full" data-testid="partnership-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : L("Submit Partnership Request", "साझेदारी अनुरोध भेजें")}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
